import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_URL = import.meta.env.VITE_API_URL;

const ReclamationFormModal = ({
  clientId: initialClientId,
  dossierId: initialDossierId,
  reclamationToEdit = null,
  onClose,
  onSuccess
}) => {
  const getToken = () => localStorage.getItem('token');

  // Recherche Autocomplete Client
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Liste des dossiers pour le client sélectionné
  const [clientDossiers, setClientDossiers] = useState([]);
  const [loadingDossiers, setLoadingDossiers] = useState(false);
  const [usersList, setUsersList] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    clientId: initialClientId || '',
    dossierId: initialDossierId || '',
    sujet: '',
    message: '',
    categorie: 'Dossier',
    priorite: 'medium',
    statut: 'open',
    assignedTo: ''
  });

  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 1. Charger les utilisateurs autorisés pour l'affectation
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setUsersList(list.filter(u => ['admin', 'moderateur', 'super_admin'].includes((u.typeUser || '').toLowerCase())));
    } catch (_) {
      // silencieux
    }
  };

  // 2. Si initialClientId est fourni, charger les détails du client
  useEffect(() => {
    if (initialClientId) {
      fetchClientDetails(initialClientId);
    }
  }, [initialClientId]);

  // 3. Pré-remplissage en cas de modification
  useEffect(() => {
    if (reclamationToEdit) {
      setFormData({
        clientId: reclamationToEdit.client?._id || reclamationToEdit.client || '',
        dossierId: reclamationToEdit.dossier?._id || reclamationToEdit.dossier || '',
        sujet: reclamationToEdit.sujet || '',
        message: reclamationToEdit.message || '',
        categorie: reclamationToEdit.categorie || 'Dossier',
        priorite: reclamationToEdit.priorite || 'medium',
        statut: reclamationToEdit.statut || 'open',
        assignedTo: reclamationToEdit.assignedTo?._id || reclamationToEdit.assignedTo || ''
      });
      if (reclamationToEdit.client) {
        setSelectedClient(reclamationToEdit.client);
        if (reclamationToEdit.client._id) {
          fetchClientDossiers(reclamationToEdit.client._id);
        }
      }
    }
  }, [reclamationToEdit]);

  const fetchClientDetails = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/clients/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const c = res.data?.data || res.data;
      if (c) {
        setSelectedClient(c);
        setFormData(prev => ({ ...prev, clientId: c._id }));
        fetchClientDossiers(c._id);
      }
    } catch (err) {
      console.error('Erreur chargement détails client:', err);
    }
  };

  // 4. Charger les dossiers du client
  const fetchClientDossiers = async (cId) => {
    if (!cId) return;
    setLoadingDossiers(true);
    try {
      const res = await axios.get(`${API_URL}/reclamations/client-dossiers/${cId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setClientDossiers(res.data || []);
    } catch (err) {
      console.error('Erreur chargement dossiers client:', err);
      setClientDossiers([]);
    } finally {
      setLoadingDossiers(false);
    }
  };

  // 5. Autocomplete recherche client
  useEffect(() => {
    if (initialClientId || selectedClient) return;
    if (!clientSearchQuery || clientSearchQuery.trim().length < 2) {
      setClientSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingClients(true);
      try {
        const res = await axios.get(`${API_URL}/reclamations/search-clients?query=${encodeURIComponent(clientSearchQuery.trim())}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setClientSearchResults(res.data || []);
      } catch (err) {
        console.error('Erreur recherche clients:', err);
      } finally {
        setSearchingClients(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [clientSearchQuery, initialClientId, selectedClient]);

  const handleSelectClient = (c) => {
    setSelectedClient(c);
    setFormData(prev => ({ ...prev, clientId: c._id, dossierId: '' }));
    setClientSearchQuery('');
    setClientSearchResults([]);
    fetchClientDossiers(c._id);
  };

  const handleClearClient = () => {
    if (initialClientId) return; // verrouillé si dans context client
    setSelectedClient(null);
    setClientDossiers([]);
    setFormData(prev => ({ ...prev, clientId: '', dossierId: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.clientId) {
      Swal.fire('Erreur', 'Veuillez sélectionner un client', 'warning');
      return;
    }
    if (!formData.sujet.trim() || !formData.message.trim()) {
      Swal.fire('Erreur', 'Le sujet et la description sont obligatoires', 'warning');
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);

    try {
      const data = new FormData();
      data.append('client', formData.clientId);
      data.append('dossier', formData.dossierId || '');
      data.append('sujet', formData.sujet.trim());
      data.append('message', formData.message.trim());
      data.append('categorie', formData.categorie);
      data.append('priorite', formData.priorite);
      data.append('statut', formData.statut);
      data.append('assignedTo', formData.assignedTo || '');

      files.forEach(f => {
        data.append('attachments', f);
      });

      let response;
      if (reclamationToEdit) {
        response = await axios.put(`${API_URL}/reclamations/${reclamationToEdit._id}`, data, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (pEvent) => {
            if (pEvent.total) {
              setUploadProgress(Math.round((pEvent.loaded * 100) / pEvent.total));
            }
          }
        });
      } else {
        response = await axios.post(`${API_URL}/reclamations`, data, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (pEvent) => {
            if (pEvent.total) {
              setUploadProgress(Math.round((pEvent.loaded * 100) / pEvent.total));
            }
          }
        });
      }

      Swal.fire({
        title: 'Succès !',
        text: response.data.message || 'Réclamation enregistrée avec succès',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      if (onSuccess) onSuccess(response.data.reclamation);
      onClose();
    } catch (err) {
      console.error('Erreur soumission réclamation:', err);
      const errMsg = err.response?.data?.message || 'Impossible d\'enregistrer la réclamation';
      Swal.fire('Erreur', errMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rec-modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
      <div className="rec-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px', width: '92%', borderRadius: '16px', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1.5px solid #e2e8f0' }}>
          <h2 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.25rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{reclamationToEdit ? '📝' : '➕'}</span>
            {reclamationToEdit ? `Modifier la Réclamation ${reclamationToEdit.reference || ''}` : 'Nouvelle Réclamation Client'}
          </h2>
          <button type="button" onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontWeight: 700, color: '#64748b' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          {/* SÉLECTION CLIENT */}
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>
              Client Concerné *
            </label>

            {selectedClient ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: '10px' }}>
                <div>
                  <strong style={{ color: '#1e3a5f', fontSize: '0.95rem' }}>{selectedClient.prenom} {selectedClient.nom}</strong>
                  <div style={{ fontSize: '0.8rem', color: '#3b82f6', marginTop: '2px' }}>
                    📞 {selectedClient.telephone || 'N/A'} | CIN: {selectedClient.cin || 'N/A'} | Type: {selectedClient.typeClient || 'BT'}
                  </div>
                </div>
                {!initialClientId && !reclamationToEdit && (
                  <button type="button" onClick={handleClearClient} style={{ background: '#dbeafe', border: 'none', color: '#1d4ed8', borderRadius: '6px', padding: '4px 8px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
                    Changer
                  </button>
                )}
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="🔍 Rechercher un client par Nom, Prénom, Téléphone, CIN..."
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
                {searchingClients && <small style={{ position: 'absolute', right: '12px', top: '12px', color: '#64748b' }}>Recherche...</small>}

                {clientSearchResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: '200px', overflowY: 'auto', marginTop: '4px' }}>
                    {clientSearchResults.map(c => (
                      <div
                        key={c._id}
                        onClick={() => handleSelectClient(c)}
                        style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s' }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.background = '#ffffff'}
                      >
                        <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{c.prenom} {c.nom}</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>📞 {c.telephone} | CIN: {c.cin} | Dossiers: {c.dossiersCount}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SÉLECTION DOSSIER */}
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>
              Dossier Concerné (Optionnel)
            </label>
            <select
              value={formData.dossierId}
              onChange={(e) => setFormData({ ...formData, dossierId: e.target.value })}
              disabled={!selectedClient || loadingDossiers}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', background: (!selectedClient || loadingDossiers) ? '#f8fafc' : '#ffffff', boxSizing: 'border-box' }}
            >
              <option value="">-- Aucun dossier spécifique (Réclamation Globale Client) --</option>
              {clientDossiers.map(d => (
                <option key={d._id} value={d._id}>
                  {d.referenceSteg || 'Dossier Pompage'} ({d.typePaiement?.toUpperCase() || 'COMPTANT'}) — Statut: {d.etatDossier}
                </option>
              ))}
            </select>
            {loadingDossiers && <small style={{ color: '#64748b', fontSize: '0.78rem' }}>Chargement des dossiers du client...</small>}
          </div>

          {/* TITRE ET CATÉGORIE */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>Sujet / Titre *</label>
              <input
                type="text"
                required
                placeholder="Ex: Retard d'approbation STEG..."
                value={formData.sujet}
                onChange={(e) => setFormData({ ...formData, sujet: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>Catégorie *</label>
              <select
                value={formData.categorie}
                onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              >
                <option value="Dossier">📂 Dossier</option>
                <option value="Paiement">💳 Paiement</option>
                <option value="STEG">⚡ STEG</option>
                <option value="Installation">🛠️ Installation</option>
                <option value="Matériel">📦 Matériel</option>
                <option value="Facturation">📄 Facturation</option>
                <option value="Livraison">🚚 Livraison</option>
                <option value="SAV">🔧 SAV</option>
                <option value="Commercial">🤝 Commercial</option>
                <option value="Administratif">📝 Administratif</option>
                <option value="Autre">📌 Autre</option>
              </select>
            </div>
          </div>

          {/* PRIORITÉ, STATUT ET AFFECTATION */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>Priorité *</label>
              <select
                value={formData.priorite}
                onChange={(e) => setFormData({ ...formData, priorite: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              >
                <option value="low">🟢 Faible</option>
                <option value="medium">🟡 Normale</option>
                <option value="high">🟠 Élevée</option>
                <option value="critical">🔥 CRITIQUE</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>Statut</label>
              <select
                value={formData.statut}
                onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              >
                <option value="open">Ouverte</option>
                <option value="in_progress">En cours</option>
                <option value="waiting">En attente</option>
                <option value="resolved">Résolue</option>
                <option value="closed">Clôturée</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>Assigner à</label>
              <select
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              >
                <option value="">-- Non assigné --</option>
                {usersList.map(u => (
                  <option key={u._id} value={u._id}>
                    {u.prenom} {u.nom} ({u.typeUser})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* DESCRIPTION */}
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>Description Complète *</label>
            <textarea
              required
              rows={4}
              placeholder="Détaillez clairement le problème ou la réclamation du client..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          {/* PIÈCES JOINTES */}
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'block' }}>
              Pièces jointes (Optionnel, Max 5 fichiers)
            </label>
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={(e) => setFiles(Array.from(e.target.files))}
              style={{ padding: '8px', background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}
            />
            {files.length > 0 && (
              <div style={{ marginTop: '6px', fontSize: '0.82rem', color: '#059669', fontWeight: 600 }}>
                ✓ {files.length} fichier(s) sélectionné(s) : {files.map(f => f.name).join(', ')}
              </div>
            )}
          </div>

          {submitting && uploadProgress > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#1e3a5f', fontWeight: 700, marginBottom: '4px' }}>
                <span>Transfert et enregistrement...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#2563eb' }}></div>
              </div>
            </div>
          )}

          {/* BOUTONS D'ACTION */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#1e3a5f', color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(30, 58, 95, 0.25)' }}
            >
              {submitting ? 'Enregistrement...' : (reclamationToEdit ? 'Enregistrer les modifications' : 'Créer la réclamation')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReclamationFormModal;
